import React, { useEffect, useMemo, useState } from "react";
import {
  ArticleCard,
  Loader,
  DropDown,
  Pagination,
} from "@relentless/rmds/v1/relentless.esm.js";
import FilterBar from "../components/FilterBar.jsx";
import { HeroBanner } from "../components/SiteUI";

import { useBlogPosts } from "../lib/api";
import { REVEAL_ANIMATIONS, useStaggeredReveal } from "../lib/animations";
import styles from "./BlogPage.module.scss";

export default function BlogPage() {
  const { posts: cmsPosts, loading: postsLoading } = useBlogPosts();
  const FEATURED_FILTER = "Featured";
  const [selectedCats, setSelectedCats] = useState([]);
  const [sortBy, setSortBy] = useState("Newest to oldest");
  const [sortOpen, setSortOpen] = useState(false);
  const [pageSize, setPageSize] = useState("All");
  const [page, setPage] = useState(1);

  const sortOptions = [
    { label: "Newest to oldest", value: "Newest to oldest", iconLeft: "event" },
    { label: "Oldest to newest", value: "Oldest to newest", iconLeft: "schedule" },
    { label: "Category", value: "Category", iconLeft: "local_offer" },
  ];
  const pageSizeOptions = ["All", "5", "10", "20", "50"];

  const sortedPosts = useMemo(() => {
    const all = cmsPosts || [];
    const toPublishedTime = (post) => {
      const raw = post?.publishedAt || "";
      const parsed = Date.parse(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const getCategory = (post) => {
      if (post?.cat) return post.cat;
      const cats = Array.isArray(post?.categories) ? post.categories : [];
      return cats[0] || "";
    };
    return [...all].sort((a, b) => {
      if (sortBy === "Category") {
        return String(getCategory(a)).localeCompare(String(getCategory(b)));
      }
      if (sortBy === "Oldest to newest") {
        return toPublishedTime(a) - toPublishedTime(b);
      }
      return toPublishedTime(b) - toPublishedTime(a);
    });
  }, [cmsPosts, sortBy]);

  const allCategories = useMemo(() => {
    const flat = (cmsPosts || []).flatMap((p) => {
      const cats = Array.isArray(p.categories) ? p.categories : [];
      return p.cat ? [...cats, p.cat] : cats;
    });
    const unique = Array.from(new Set(flat.filter(Boolean)));

    return unique;
  }, [cmsPosts]);

  const categoryOptions = useMemo(() => {
    const list = ["All", FEATURED_FILTER, ...(allCategories || [])];
    return list.map((label) => ({
      label,
      value: label,
      iconLeft:
        label === "All"
          ? "filter_alt"
          : label === FEATURED_FILTER
            ? "star"
            : "local_offer",
    }));
  }, [FEATURED_FILTER, allCategories]);

  const categoryMinWidth = useMemo(() => {
    const longest =
      categoryOptions.reduce((max, opt) => {
        const len = String(opt?.label || "").length;
        return len > max ? len : max;
      }, 0) || 0;
    return `${Math.max(14, longest + 4)}ch`;
  }, [categoryOptions]);

  const filteredPosts = useMemo(() => {
    if (!selectedCats.length) return sortedPosts || [];
    return (sortedPosts || []).filter((post) => {
      if (selectedCats.includes(FEATURED_FILTER) && post?.featured) {
        return true;
      }
      const cats = Array.isArray(post.categories) ? post.categories : [];
      const combined = post.cat ? [...cats, post.cat] : cats;
      return combined.some((c) => selectedCats.includes(c));
    });
  }, [FEATURED_FILTER, selectedCats, sortedPosts]);

  const heroPosts = useMemo(() => {
    const all = sortedPosts || [];
    if (!all.length) return [];
    const featured = all.filter((post) => post?.featured);
    return featured;
  }, [sortedPosts]);

  const heroImages = useMemo(() => {
    const slides =
      heroPosts
        .filter(Boolean)
        .map((p) => ({
          src:
            p.imgeUrl ||
            `https://picsum.photos/seed/${p.seed || p.slug || p.id}/1600/600`,
          title: p.title,
          subtitle: p.subtitle || p.cat || "",
          body: p.content || p.body || p.excerpt || "",
          cta: {
            label: "Read more",
            href: `/blog/${p.slug || p.id}`,
          },
        }))
        .filter((s) => Boolean(s.src)) || [];

    if (slides.length >= 3) return slides.slice(0, 3);
    if (slides.length > 0) return slides;

    return [];
  }, [heroPosts]);

  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    if (!heroImages.length) return;
    const id = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(id);
  }, [heroImages]);

  const totalPages = useMemo(() => {
    if (pageSize === "All") return 1;
    const size = Number(pageSize) || 1;
    return Math.max(1, Math.ceil(filteredPosts.length / size));
  }, [filteredPosts.length, pageSize]);

  const posts = useMemo(() => {
    if (pageSize === "All") return filteredPosts;
    const size = Number(pageSize) || 1;
    const start = (page - 1) * size;
    return filteredPosts.slice(start, start + size);
  }, [filteredPosts, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [selectedCats, sortBy, pageSize]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!sortOpen) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(`.${styles.sortWrap}`)) return;
      setSortOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [sortOpen]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useStaggeredReveal(styles.rowOverlap, [posts], {
    mediaSelector: ".card_media",
    bodySelector: ".card_body",
  });

  useEffect(() => {
    const container = document.querySelector(`.${styles.rowOverlap}`);
    if (!container) return undefined;

    const measureAndSet = () => {
      const cards = Array.from(container.querySelectorAll(".article_card"));
      if (!cards.length) return;
      const heights = cards.map((el) => el.getBoundingClientRect().height || 0);
      const minHeight = Math.min(...heights.filter((h) => h > 0));
      if (!Number.isFinite(minHeight) || minHeight <= 0) return;
      container.style.setProperty("--blog-card-height", `${minHeight}px`);
    };

    const raf = requestAnimationFrame(measureAndSet);
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measureAndSet);
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [posts.length]);

  // Newsletter handled within BlogAside
  const hasPosts = (cmsPosts || []).length > 0;
  if (postsLoading && !hasPosts) {
    return (
      <div className="rm-page-loading">
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.hero} aria-label="Featured posts">
        <HeroBanner
          className="center"
          images={heroImages}
          title={heroImages[heroSlide]?.title}
          subtitle={heroImages[heroSlide]?.subtitle}
          autoScroll={10000}
          contentAlign="bottom-left"
          onHeroClick={(event, { index }) => {
            const post = heroPosts[index] || heroPosts[0];
            if (!post) return;
            window.history.pushState({}, "", `/blog/${post.slug || post.id}`);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
        />
      </section>

      <main className={`${styles.content} grid`}>
        <FilterBar className={styles.filters}>
          <div className={styles.categoryWrap}>
            <DropDown
              label="Filter by"
              labelPosition="left"
              displayMode="icon"
              iconOnly={true}
              value={selectedCats[0] || "All"}
              options={categoryOptions}
              style={{ minWidth: categoryMinWidth }}
              onSelect={(val) => {
                setSelectedCats(val === "All" ? [] : [val]);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedCats(val === "All" ? [] : [val]);
              }}
            />
          </div>
          <div className={styles.sortWrap}>
            <div className={styles.sortMenu}>
              <DropDown
                label="Sort by"
                labelPosition="left"
                displayMode="icon"
                iconOnly={true}
                value={sortBy}
                options={sortOptions}
                onSelect={(val) => {
                  setSortBy(val);
                  setSortOpen(false);
                }}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setSortOpen(false);
                }}
              />
            </div>
          </div>
        </FilterBar>
        <aside className={styles.filterRail} aria-label="Blog filters">
          <section className={styles.filterGroup} aria-labelledby="blog-filter-heading">
            <h2 id="blog-filter-heading" className="t-caption">
              Filter by
            </h2>
            <div className={styles.filterList}>
              {categoryOptions.map((option) => {
                const value = option.value;
                const isActive =
                  value === "All"
                    ? selectedCats.length === 0
                    : selectedCats.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`${styles.filterOption} ${
                      isActive ? styles.filterOptionActive : ""
                    }`}
                    aria-pressed={isActive}
                    onClick={() => {
                      setSelectedCats(value === "All" ? [] : [value]);
                    }}
                  >
                    <span className={styles.optionCheck} aria-hidden="true" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className={styles.filterGroup} aria-labelledby="blog-sort-heading">
            <h2 id="blog-sort-heading" className="t-caption">
              Sort by
            </h2>
            <div className={styles.filterList}>
              {sortOptions.map((option) => {
                const isActive = sortBy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.filterOption} ${
                      isActive ? styles.filterOptionActive : ""
                    }`}
                    aria-pressed={isActive}
                    onClick={() => setSortBy(option.value)}
                  >
                    <span className={styles.optionCheck} aria-hidden="true" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>
        <section className={`${styles.mainGrid} grid`}>
          <div className={`${styles.rowOverlap} grid`}>
            {postsLoading && !posts.length
              ? Array.from({ length: 3 }, (_, i) => (
                  <Loader key={`blog-loader-${i}`} />
                ))
              : posts.map((a, index) => (
                  <div
                    key={`${a.id}`}
                    className={styles.articleReveal}
                    data-animation={REVEAL_ANIMATIONS.slideInUp}
                    data-reveal-index={index}
                  >
                    <ArticleCard
                      variant="a_card"
                      imageTop={true}
                      eyebrow={a.featured ? "Featured" : undefined}
                      title={a.title}
                      content={a.excerpt}
                      imageUrl={a.imgeUrl}
                      metaRows={[a.published, a.cat].filter((val) =>
                        typeof val === "string"
                          ? val.trim().length
                          : Boolean(val),
                      )}
                      ctaLabel="Read more"
                      ctaProps={{
                        href: `/blog/${a.slug || a.id}`,
                        link: true,
                      }}
                    />
                  </div>
                ))}
          </div>
          {totalPages > 1 && (
            <div className={styles.bodyFooter}>
              <Pagination
                total={totalPages}
                current={page}
                onChange={(next) => setPage(next)}
                pageCount={5}
                showPageSize
                showStatus
                pageSizeOptions={pageSizeOptions}
                pageSize={pageSize}
                onPageSizeChange={(val) => setPageSize(val)}
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
